/*
 * draft-order-checkout-reib.js
 * ---------------------------------------------------------------------------
 * Reichenbach-specific draft-order checkout integration.
 *
 * Why this file exists
 * --------------------
 * Configurable products on this store (Zahnriemen / Transportband / …) have
 * a price that is computed on the PDP from user input (width, length,
 * connection type, …) and stored on the cart line as the hidden line-item
 * property `_p`. Shopify's own checkout can only see the variant price, so
 * without this script the customer would end up paying the wrong amount.
 *
 * The fix in the old theme lived in `assets/combined.js`:
 *
 *   - When the user clicks the "Zur Kasse" (checkout) button,
 *     intercept the form submit.
 *   - POST the Shopify cart (as JSON) to the custom Shopify-App at
 *     `/api/v1/checkout`.
 *   - The app builds a Shopify draft order with the correctly calculated
 *     prices and returns `{ invoiceUrl: "<draft-order-invoice-url>" }`.
 *   - The browser is redirected to `invoiceUrl`; the customer completes
 *     the order in a normal Shopify checkout (now with the right total).
 *
 * Scope
 * -----
 * The intercept is ONLY activated when the server-rendered cart already
 * contains at least one line item with `_p`. The cart-drawer / main-cart
 * -reib Liquid sections announce this by setting
 *   <form ... data-reib-has-custom-price="true">
 * on the checkout form. If the attribute is absent or equals "false" we
 * do nothing and the standard Shopify checkout flow (POST /cart + submit
 * name=checkout) runs unmodified.
 *
 * Section-rendering API
 * ---------------------
 * The cart drawer HTML is re-rendered by assets/cart.js after every cart
 * mutation, so this script's delegated click listener keeps working
 * automatically (it lives on `document`, not on any specific node that
 * gets replaced).
 */
(function () {
  "use strict";

  /*
    Production endpoint of the Reichenbach Draft-Order Shopify App
    (hosted on Railway). If Railway re-issues a new URL it must be
    updated here (same rule as the old theme, see
    `Dokumentation-Draft-Order-App (von TB).pdf`, section "Deployment").
  */
  var APP_CHECKOUT_ENDPOINT = "https://reichenbach-shopify-app-draftorder-production.up.railway.app/api/v1/checkout"; // production (new)
  // var APP_CHECKOUT_ENDPOINT = "https://reichenbach-shopify-app-draftorder-development.up.railway.app/api/v1/checkout"; // development

  var ERROR_MESSAGE =
    "Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.";

  /*
    We keep one in-flight flag at module scope. Shopify's cart-drawer
    and the cart page can both be visible at the same time (cart page
    with drawer closed is the norm, but the button is duplicated), so
    the same state must guard both.
  */
  var requestInFlight = false;

  function getCheckoutForm(button) {
    /*
      The button either lives inside its own <form> (drawer) or uses
      the HTML `form="cart"` attribute to reference the main cart form.
    */
    if (button.form) return button.form;
    return button.closest("form");
  }

  function formWantsCustomCheckout(form) {
    if (!form) return false;
    var attr = form.getAttribute("data-reib-has-custom-price");
    return attr === "true" || attr === "1";
  }

  function setLoading(button, loading) {
    if (!button) return;
    if (loading) {
      button.setAttribute("aria-disabled", "true");
      button.setAttribute("data-reib-loading", "true");
      button.classList.add("btn--loading");
    } else {
      button.removeAttribute("aria-disabled");
      button.removeAttribute("data-reib-loading");
      button.classList.remove("btn--loading");
    }
  }

  function renderError(form, message) {
    if (!form) return;
    var existing = form.querySelector(".js-reib-draft-order-error");
    if (!existing) {
      existing = document.createElement("div");
      existing.className =
        "js-reib-draft-order-error alert alert--error mt-2";
      existing.setAttribute("role", "alert");
      form.appendChild(existing);
    }
    existing.textContent = message;
    existing.hidden = false;
  }

  function clearError(form) {
    if (!form) return;
    var existing = form.querySelector(".js-reib-draft-order-error");
    if (existing) existing.remove();
  }

  async function fetchCart() {
    var res = await fetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("Failed to fetch /cart.js (" + res.status + ")");
    return res.json();
  }

  async function requestDraftOrder(cart) {
    var res = await fetch(APP_CHECKOUT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(cart),
    });
    if (!res.ok) {
      throw new Error(
        "Draft-order endpoint returned HTTP " + res.status
      );
    }
    var data = await res.json();
    if (!data || !data.invoiceUrl) {
      throw new Error("Draft-order endpoint response missing invoiceUrl");
    }
    return data.invoiceUrl;
  }

  async function handleCheckoutClick(event) {
    var button = event.target.closest && event.target.closest('button[name="checkout"]');
    if (!button) return;

    var form = getCheckoutForm(button);

    /*
      Fast path: cart has no configurable lines → let the browser
      submit the form normally (Shopify's own checkout flow). We do
      NOT preventDefault; this keeps the native redirect behaviour
      intact and avoids an unnecessary /cart.js round-trip.
    */
    if (!formWantsCustomCheckout(form)) return;

    event.preventDefault();
    event.stopPropagation();

    if (requestInFlight) return;
    requestInFlight = true;

    clearError(form);
    setLoading(button, true);

    try {
      var cart = await fetchCart();

      /*
        Defensive re-check: if the page is out of date (item removed
        in another tab, say) and the cart actually no longer contains
        a `_p` line, fall back to the native checkout instead of
        calling the draft-order endpoint.
      */
      var stillHasCustom = Array.isArray(cart.items) && cart.items.some(function (it) {
        return it.properties && it.properties._p;
      });
      if (!stillHasCustom) {
        window.location.assign("/checkout");
        return;
      }

      var invoiceUrl = await requestDraftOrder(cart);
      window.location.assign(invoiceUrl);
    } catch (err) {
      console.error("[draft-order-checkout-reib]", err);
      renderError(form, ERROR_MESSAGE);
      setLoading(button, false);
      requestInFlight = false;
    }
    /*
      Intentionally no finally{} to reset loading state on success:
      after window.location.assign() we WANT the button to stay
      disabled until the navigation happens, otherwise double clicks
      can create duplicate draft orders.
    */
  }

  /*
    Capture phase so we run before any other framework-level handler
    (e.g. a hypothetical analytics click listener on the same button).
    The delegated listener is attached once; when the cart drawer
    re-renders its inner HTML via assets/cart.js the listener stays
    valid because it is bound on `document`.
  */
  document.addEventListener("click", handleCheckoutClick, true);
})();
